import React, { useEffect, useState } from 'react';
import { TextInput, Form, Button, Table, Select } from '@contentful/f36-components';
import { MultiSelect } from 'react-multi-select-component';
import * as searchQuery from '../SearchQuery.js';
import { post } from '../http.js';
import styles from '../SearchPage.module.css';
import loaderGif from '../assets/loader.gif';

const Page = () => {
  var tableData = [];
  var searchType, query, typeOfContent = "";
  const subcategoryLimit = 20;
  const limitToFetchId = "limit:10000";
  const pilContentType = "pagePatientInformationLeaflet";
  const mlContentType = "pageMedicineLeaflet";
  const feaContentType = "pageFeatured";
  const profArticleContentType = "pagePatientProfessionalArticle";
  const contentKeywordSearch = "ContentType-SearchKeyword";
  const contentCategoryKeywordSearch = "Content-Category-Search";
  const contentSubcategorySearch = "Content-Subcategory-Search";

  const [selectedContentType, setContentType] = useState({ contentType_ID: pilContentType });
  const [categoryObj, setCategoryObj] = useState({ category_Id: '', category_Name: '', subCategory_Id: [], });
  const [categories, setCategoryData] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [searchText, setSearchText] = useState({ searchValue: '' });
  const [selectedSubCategory, setSelectedSubCategory] = useState([])
  const [lstItems, setTableData] = useState([]);
  const [contents, setContentIds] = useState({ lstContentIds: '', lstContentIdsCount: 0 });
  const [errorValue, setErrorMessage] = useState({ errorMessage: '' });
  const [noResponse, setInfoMessage] = useState({ noResponseMessage: '' });
  const [showcontentIds, setshowHideIds] = useState(false);
  const [isLoadMore, setIsLoadMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [skip, setSkip] = useState(0);
  let limit = "limit:100";
  let skipCount = `skip:${skip}`;

  const options = [];
  // track ids to avoid duplicate childsubcategories
  const filterSubCategoryIds = new Set();

  subCategories.forEach((sub) => {
    // Add parent subcategory
    if (!filterSubCategoryIds.has(sub.sys?.id)) {
      options.push({ label: sub.name, value: sub.sys?.id });
      filterSubCategoryIds.add(sub.sys?.id);
    }

    // Add child subcategories
    sub?.childSubcategoriesCollection?.items?.forEach((child) => {
      if (!filterSubCategoryIds.has(child.sys?.id)) {
        options.push({ label: child.name, value: child.sys?.id });
        filterSubCategoryIds.add(child.sys?.id);
      }
    });
  });
  options.sort();

  const fetchData = async () => {
    setIsLoading(true);
    try {
      setCategoryData([]);
      bindCategoryDropdown(searchQuery.categoryQuery());
    } finally {
      setIsLoading(false);
    }
  };

  // on change content type
  const setOnChangeContentType = async (event) => {
    const selectedType = event.target.value;
    setContentType({ contentType_ID: selectedType });
    setCategoryObj({ category_Id: '', category_Name: '', subCategory_Id: [] });
    setSearchText({ searchValue: '' });
    setSubCategories([]);
    setSelectedSubCategory([]);
    setCategoryData([]);
    let query;
    if (selectedType === profArticleContentType) {
      query = searchQuery.subCategoryForProfessionalArticleQuery();
    } else if (selectedType === mlContentType) {
      query = searchQuery.subCategoryForMlArticleQuery();
    } else {
      query = searchQuery.categoryQuery();
    }
    bindCategoryDropdown(query);
    fetchSubCategoriesForContentType();
  };

  const bindCategoryDropdown = async (query) => {
      const response = await post(query);
      if (response?.data?.topicCategoryCollection) {
        setCategoryData(response.data.topicCategoryCollection.items);
        setCategoryObj({ category_Id: '', category_Name: '', subCategory_Id: [] });
      } else {
        setCategoryData([]);
        setSubCategories([]);
    }
  };

  const fetchSubCategoriesForContentType = async () => {
    let subCategoryQuery = "";
    let response = {};
    // determine query based on content type
    if (
      selectedContentType.contentType_ID === pilContentType ||
      selectedContentType.contentType_ID === feaContentType
    ) {
      subCategoryQuery = searchQuery.getSubCategoryForPILorFEA();
      response = await post(subCategoryQuery);
    }
    if (response?.data?.topicCategoryCollection) {
      setSubCategories(response.data.topicCategoryCollection.items);
    } else {
      setSubCategories([]);
    }
  };

  // On change category type
  const setOnChangeCategoryType = async (e) => {
    setSkip(0);
    const selectedCategoryId = e.target.value;
    setCategoryObj({ category_Id: selectedCategoryId, category_Name: e.target.selectedOptions[0]?.label || '', subCategory_Id: [], });
    setSelectedSubCategory([]);
    if (selectedContentType.contentType_ID === profArticleContentType || selectedContentType.contentType_ID === mlContentType) {
      setSubCategories([]);
      return;
    }

    // Fetch all subcategories if no category is selected
    if (!selectedCategoryId) {
      await fetchSubCategoriesForContentType();
      return;
    }

    // Fetch subcategories for selected category
    const response = await post(searchQuery.selectedCategoryQuery(selectedCategoryId));
    if (response?.data?.topicCategoryCollection?.items[0]?.childSubcategoriesCollection) {
      setSubCategories(response.data.topicCategoryCollection.items[0].childSubcategoriesCollection.items);
    }
    else {
      setSubCategories([]);
    }
  };

  // On change subcategory type
  const setOnChangeSubCategoryType = (selectedOptions) => {
    setSkip(0);
    const selectedSubCategoryIds = selectedOptions.map((option) => option.value);
    setSelectedSubCategory(selectedSubCategoryIds);
    setCategoryObj({ ...categoryObj, subCategory_Id: selectedSubCategoryIds });
  };

  // to populate the categories in the dropdown in the load.
  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchSubCategoriesForContentType();
  }, [selectedContentType.contentType_ID]);

  useEffect(() => {
    setSkip(0);
  }, [searchText]);

  // to handle the search click event with the two cases.
  const handleSearch = async () => {
    setIsLoading(true);
    // to handle search when all contents are loaded(loadmore), so need to reset skip/limit and set to entire length
    if (lstItems.length === contents.lstContentIdsCount && skip !== 0) {
      limit = `limit:${lstItems.length}`
      skipCount = `skip:${0}`
    }
    try {
      setInfoMessage({ noResponseMessage: "" });
      setContentIds({ lstContentIds: "" });
      let subCategoryIds = []; // Initialize as an array to hold subcategory IDs
      let subCategoryIdQuery = "";
      // if no input is given, show error message
      if (!searchText.searchValue && !categoryObj.category_Id && categoryObj.subCategory_Id.length === 0) {
        setErrorMessage({ errorMessage: "Please enter a keyword or Select any Category/Subcategory to search" });
        setTableData([]);
      }
      else {
        setErrorMessage({ errorMessage: "" });
        selectedContentType.contentType_ID = !selectedContentType.contentType_ID ? pilContentType : selectedContentType.contentType_ID;
        searchType = getSearchTypes();
        switch (selectedContentType.contentType_ID) {
          case pilContentType:
            typeOfContent = "PatientInformationLeaflet";
            break;
          case profArticleContentType:
            typeOfContent = "ProfessionalArticle";
            break;
          case feaContentType:
            typeOfContent = "FeatureArticle";
            break;
          default:
            typeOfContent = "";
            break;
        }

        if (categoryObj.subCategory_Id.length > 0
          && categoryObj.subCategory_Id.length <= subcategoryLimit
        ) {
          // When a specific subcategory is selected
          subCategoryIds = categoryObj.subCategory_Id;
          subCategoryIds = JSON.stringify(subCategoryIds);
          // Join subcategory IDs with commas and format the query
          subCategoryIdQuery = searchQuery.subCategoryQuery(subCategoryIds);
        }
        else if (categoryObj.subCategory_Id.length > subcategoryLimit) {
          setErrorMessage({ errorMessage: `Please select no more than ${subcategoryLimit} subcategories` });
          setTableData([]);
          searchType = "";
        }
      }

      switch (searchType) {
        case contentKeywordSearch:
          query = searchQuery.getContentTypeKeywordQuery(
            selectedContentType.contentType_ID === feaContentType ? pilContentType : selectedContentType.contentType_ID,
            subCategoryIdQuery,
            typeOfContent,
            searchText.searchValue,
            limit,
            skipCount
          );
          if (selectedContentType.contentType_ID === mlContentType || selectedContentType.contentType_ID === profArticleContentType) {
            query = searchQuery.getContentTypeMlQuery(selectedContentType.contentType_ID, searchText.searchValue, limit, skipCount);
          }
          getContentTypesWithKeyword(query);
          return contentKeywordSearch;

        case contentCategoryKeywordSearch:
          getContentAndCategoryResponse();
          return contentCategoryKeywordSearch;

        case contentSubcategorySearch:
          getContentAndCategoryResponse();
          return contentSubcategorySearch;
        default:
          break;
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getSearchTypes = () => {
    if (selectedContentType.contentType_ID && searchText.searchValue && !categoryObj.category_Id) {
      searchType = contentKeywordSearch;
    } else if (selectedContentType.contentType_ID && categoryObj.category_Id) {
      searchType = contentCategoryKeywordSearch;
    } else if (selectedContentType.contentType_ID && !searchText.searchValue && !categoryObj.category_Id && categoryObj.subCategory_Id) {
      searchType = contentSubcategorySearch;
    }

    return searchType;
  };

  // to fetch the results for the content types with the keyword.
  const getContentTypesWithKeyword = async (query) => {
    const data = await post(query);
    bindTableData(data);
    // to get only ids to show in textarea when content type and keyword search.
    getContentTypesWithKeywordIdList();
  };

  // to get only ids to show in textarea.
  const getContentTypesWithKeywordIdList = async () => {
    const data = await post(getContentTypeIdsSearchQuery(limitToFetchId));
    bindContentIds(data);
  };

  const getContentTypeIdsSearchQuery = (limit) => {
    let subCategoryIds = [];
    let subCategoryIdQuery = "";
    if (categoryObj.subCategory_Id.length > 0
      && categoryObj.subCategory_Id.length <= subcategoryLimit
    ) {
      // When a specific subcategory is selected
      subCategoryIds = categoryObj.subCategory_Id;
      subCategoryIds = JSON.stringify(subCategoryIds);
      // Join subcategory IDs with commas and format the query
      subCategoryIdQuery = searchQuery.subCategoryQuery(subCategoryIds);
    }
    query = searchQuery.getContentTypeKeywordIdQuery(
      selectedContentType.contentType_ID === feaContentType ? pilContentType : selectedContentType.contentType_ID,
      subCategoryIdQuery,
      typeOfContent,
      searchText.searchValue,
      limit,
    );

    if (selectedContentType.contentType_ID === mlContentType || selectedContentType.contentType_ID === profArticleContentType) {
      query = searchQuery.getmlContentTypeKeywordIdQuery(selectedContentType.contentType_ID, subCategoryIdQuery, searchText.searchValue, limit, skipCount);
    }

    return query;
  };

  // to get the sub category ids for the selected categories.
  const getContentAndCategoryResponse = async () => {
    let subCategoryIds = []; // Initialize as an array to hold subcategory IDs
    let subCategoryIdQuery = "";

    if (selectedContentType.contentType_ID === profArticleContentType || selectedContentType.contentType_ID === mlContentType) {
      // For professional articles, no subcategories are fetched, only category is used.
      subCategoryIdQuery = searchQuery.subCategoryQuery(`"${categoryObj.category_Id}"`);

      const contentCategoryData = await post(getContentAndCategoryQuery(subCategoryIdQuery, limit, skipCount));
      bindTableData(contentCategoryData, isLoadMore);

      // Fetch IDs for content and category
      getContentAndCategoryIdList(getContentAndCategoryIdQuery(subCategoryIdQuery, limitToFetchId));
    } else {
      if (categoryObj.category_Id && categoryObj.subCategory_Id.length === 0) {
        // When category is selected but no subcategory
        const response = await post(searchQuery.selectedCategoryQuery(categoryObj.category_Id));
        if (response?.data?.topicCategoryCollection?.items[0]?.childSubcategoriesCollection) {
          response.data.topicCategoryCollection.items[0].childSubcategoriesCollection.items.forEach(element => {
            subCategoryIds.push(element.sys.id);
            element.childSubcategoriesCollection?.items.forEach(item => {
              subCategoryIds.push(item?.sys.id);
            });
          });
        }
      } else if (categoryObj.subCategory_Id.length > 0) {
        // When a specific subcategory is selected
        subCategoryIds = categoryObj.subCategory_Id;
      }
      subCategoryIds = JSON.stringify(subCategoryIds);

      // Join subcategory IDs with commas and format the query
      subCategoryIdQuery = searchQuery.subCategoryQuery(subCategoryIds);
      // Perform the query and bind table data
      const contentCategoryData = await post(getContentAndCategoryQuery(subCategoryIdQuery, limit, skipCount));
      bindTableData(contentCategoryData, isLoadMore);
      // Fetch IDs for content and category
      getContentAndCategoryIdList(getContentAndCategoryIdQuery(subCategoryIdQuery, limitToFetchId));
    }
    setIsLoadMore(false);
    setIsLoading(false);
  };

  const getContentAndCategoryQuery = (subCategoryIdquery, limit, skipCount) => {
    if (selectedContentType.contentType_ID === mlContentType || selectedContentType.contentType_ID === profArticleContentType) {
      query = searchQuery.getmlCategoryQuery(subCategoryIdquery, limit, skipCount, selectedContentType.contentType_ID, searchText.searchValue);
    } else {
      query = searchQuery.getpilCategoryQuery(
        selectedContentType.contentType_ID === feaContentType ? pilContentType : selectedContentType.contentType_ID,
        subCategoryIdquery,
        limit,
        skipCount,
        typeOfContent,
        searchText.searchValue
      );
    }

    return query;
  };

  const getContentAndCategoryIdQuery = (subCategoryIdquery, limit) => {
    if (selectedContentType.contentType_ID === mlContentType || selectedContentType.contentType_ID === profArticleContentType) {
      query = searchQuery.getmlCategoryIdQuery(subCategoryIdquery, limit, selectedContentType.contentType_ID, searchText.searchValue);
    } else {
      query = searchQuery.getpilCategoryIdQuery(
        selectedContentType.contentType_ID === feaContentType || pilContentType ? pilContentType : selectedContentType.contentType_ID,
        subCategoryIdquery,
        limit,
        typeOfContent,
        searchText.searchValue
      );
    }
    return query;
  };

  // to get the ids when search the content type and category types to show in textarea.
  const getContentAndCategoryIdList = async (query) => {
    const data = await post(query);
    bindContentIds(data);
  };

  // to bind the datas into the table
  const bindTableData = (tableDataArr, isLoadMore) => {
    tableData = gettableData(tableDataArr);
    if (tableData?.items?.length) {
      // to set the response from the url into the table.
      setTableData(isLoadMore ? [...lstItems, ...tableData.items] : tableData.items);
    } else if (!isLoadMore) {
      setTableData([]);
      setInfoMessage({ noResponseMessage: "(0) Results found" });
    }

    // set isLoadMore to false after binding data
    if (isLoadMore) {
      setIsLoadMore(false);
      setIsLoading(false);
    }
  };

  const gettableData = (tableDataArr) => {
    var searchtableData = [];

    switch (selectedContentType.contentType_ID) {
      case pilContentType:
        searchtableData = tableDataArr?.data?.pagePatientInformationLeafletCollection
        return searchtableData;
      case mlContentType:
        searchtableData = tableDataArr?.data?.pageMedicineLeafletCollection
        return searchtableData;
      case profArticleContentType:
        searchtableData = tableDataArr?.data?.pagePatientProfessionalArticleCollection
        return searchtableData;
      case feaContentType:
        searchtableData = tableDataArr?.data?.pagePatientInformationLeafletCollection
        return searchtableData;
      default:
        break;
    }
  };

  const bindContentIds = (tableDataArr) => {
    var searchtableData = [];
    searchtableData = gettableData(tableDataArr);

    var contentIdsarray = [];
    if (searchtableData?.items) {
      (searchtableData.items).forEach(element => {
        contentIdsarray.push(element.sys.id);
      });
      // set the content ids into the textarea and ids count.
      setContentIds({ lstContentIds: contentIdsarray.toString().split(",").join(",  "), lstContentIdsCount: searchtableData.items.length })
    }
  };

  const loadMoreContent = () => {
    setIsLoadMore(true);
    setIsLoading(true);

    // Increment skip for pagination (fetch next set of results)
    setSkip((prevSkip) => {
      const newSkip = prevSkip + 100; // Adjust this based on your pagination logic
      return newSkip
    });
  };

  useEffect(() => {
    // Fetch more content with the updated skip value
    if (isLoadMore) {
      handleSearch();
      setIsLoadMore(false);
      setIsLoading(false);
    }
  }, [skip, isLoadMore]);

  return (
    <Form>
      {isLoading && (
        <div className={styles.loader}>
          <img src={loaderGif} alt='Loading...' />
          <h3>Loading...</h3>
        </div>
      )}
      {!isLoading && (
        <>
          <Table className={styles.table}>
            <Table.Head>
              <Table.Row>
                <Table.Cell>
                  <Select value={selectedContentType.contentType_ID} onChange={setOnChangeContentType}>
                    <Select.Option value="pagePatientInformationLeaflet">Patient Information Leaflet</Select.Option>
                    <Select.Option value="pageMedicineLeaflet">Medicine Leaflet</Select.Option>
                    <Select.Option value="pagePatientProfessionalArticle">Professional Articles</Select.Option>
                    <Select.Option value="pageFeatured">Feature Articles</Select.Option>
                  </Select>
                </Table.Cell>
                <Table.Cell>
                  <TextInput
                    value={searchText.searchValue}
                    onChange={(e) => setSearchText({ ...searchText, searchValue: e.target.value })}
                    placeholder="Search" className={styles.txtSearch}
                  />
                </Table.Cell>
                <Table.Cell>
                  <Select value={categoryObj.category_Id} onChange={setOnChangeCategoryType}>
                    <Select.Option value="">Please select Category</Select.Option>
                    {categories && categories.map(items => (
                      <Select.Option key={items.sys.id} value={items.sys.id}>
                        {items.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Table.Cell>
                <Table.Cell>
                  {selectedContentType.contentType_ID !== profArticleContentType && selectedContentType.contentType_ID !== mlContentType && (
                    <MultiSelect
                      options={options}
                      value={options.filter((option) => selectedSubCategory.includes(option.value))} // Filter to match selected values
                      onChange={setOnChangeSubCategoryType}
                      labelledBy="Please select Subcategory"
                      placeholder="Please select Subcategory"
                      className={styles.multiSelect}
                    />
                  )}
                </Table.Cell>
                <Table.Cell>
                  <Button variant="primary" className={styles.btnPrimary} onClick={handleSearch}>
                    Search
                  </Button>
                </Table.Cell>
              </Table.Row>
            </Table.Head>
          </Table>
          <div className={styles.lblError}>
            <label>{errorValue.errorMessage}</label>
          </div>
          <div className={styles.lblMessage}>
            <label>{noResponse.noResponseMessage}</label>
          </div>
          <div className={lstItems?.length ? styles.btnShowIdDisplay : styles.btnShowId}>
            <div className={styles.lblCount}>
              <label>{contents.lstContentIds ? "Total items found: " + contents.lstContentIdsCount : ""}</label>
            </div>
            <Button variant="primary" className={styles.btnPrimary} onClick={() => setshowHideIds(!showcontentIds)}>
              Show/Hide Ids
            </Button>
          </div>
          <div className={showcontentIds && lstItems?.length ? styles.divShowtextarea : styles.divtextarea}>
            <textarea className={styles.txtarea} value={contents.lstContentIds}></textarea>
          </div>
          <hr />
          <br />
          <div className={styles.lblMessage}>
            <label>{lstItems?.length && contents?.lstContentIdsCount ? `${"Showing " + lstItems.length + " items out of " + contents.lstContentIdsCount}` : ""}</label>
          </div>
          <div className={lstItems?.length ? styles.divTable : styles.divContentTable}>
            <Table className={styles.innerTable}>
              <Table.Head>
                <Table.Row>
                  <Table.Cell>Id</Table.Cell>
                  <Table.Cell>Title</Table.Cell>
                  <Table.Cell>Type</Table.Cell>
                  <Table.Cell>Path</Table.Cell>
                </Table.Row>
              </Table.Head>
              <Table.Body>
                {lstItems && lstItems.map((contentType) => {
                  return (
                    <Table.Row key={contentType.sys.id}>
                      <Table.Cell>{contentType.sys.id}</Table.Cell>
                      <Table.Cell>{contentType.title}</Table.Cell>
                      <Table.Cell>{selectedContentType.contentType_ID === mlContentType || selectedContentType.contentType_ID === profArticleContentType ? contentType.__typename : contentType.type}</Table.Cell>
                      <Table.Cell>{contentType.slug}</Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
              <br />
              {lstItems.length < contents.lstContentIdsCount && (
                <Button variant="primary" className={styles.btnLoadMore} onClick={loadMoreContent}>
                  Load More
                </Button>
              )}
            </Table>
          </div>
        </>
      )}
    </Form>
  );
};

export default Page;
